import type { InputState } from '../games/types';

const empty = (): InputState => ({
  left: false,
  right: false,
  up: false,
  down: false,
  fire: false,
  firePressed: false,
  pause: false,
});

/**
 * Merges keyboard + on-screen console pad into one InputState per frame.
 * firePressed is edge-triggered (cleared after consume/endFrame).
 */
export class InputManager {
  private keys = new Set<string>();
  private touch = {
    left: false,
    right: false,
    up: false,
    down: false,
    fire: false,
  };
  private fireEdge = false;
  private pauseEdge = false;
  private prevFire = false;

  constructor() {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.reset);
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.reset);
  }

  private onKeyDown = (e: KeyboardEvent) => {
    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', ' ', 'Spacebar'].includes(e.key)) {
      e.preventDefault();
    }
    this.keys.add(e.key.toLowerCase());
    if (e.key === ' ' || e.key.toLowerCase() === 'z' || e.key === 'Spacebar') {
      if (!this.prevFire) this.fireEdge = true;
    }
    if (e.key.toLowerCase() === 'p' || e.key === 'Escape') {
      this.pauseEdge = true;
    }
  };

  private onKeyUp = (e: KeyboardEvent) => {
    this.keys.delete(e.key.toLowerCase());
  };

  reset = (): void => {
    this.keys.clear();
    this.touch = { left: false, right: false, up: false, down: false, fire: false };
  };

  setTouch(partial: Partial<typeof this.touch>): void {
    Object.assign(this.touch, partial);
    if (partial.fire && !this.prevFire) this.fireEdge = true;
  }

  /** Call at start of frame */
  sample(): InputState {
    const left =
      this.touch.left ||
      this.keys.has('arrowleft') ||
      this.keys.has('a');
    const right =
      this.touch.right ||
      this.keys.has('arrowright') ||
      this.keys.has('d');
    const up =
      this.touch.up || this.keys.has('arrowup') || this.keys.has('w');
    const down =
      this.touch.down || this.keys.has('arrowdown') || this.keys.has('s');
    const fire =
      this.touch.fire ||
      this.keys.has(' ') ||
      this.keys.has('z') ||
      this.keys.has('spacebar');

    const state = empty();
    state.left = left;
    state.right = right;
    state.up = up;
    state.down = down;
    state.fire = fire;
    state.firePressed = this.fireEdge || (fire && !this.prevFire);
    state.pause = this.pauseEdge;
    this.fireEdge = false;
    this.pauseEdge = false;
    this.prevFire = fire;
    return state;
  }
}

export function bindPadButton(
  el: HTMLElement,
  input: InputManager,
  key: 'left' | 'right' | 'up' | 'down' | 'fire',
): void {
  const set = (v: boolean) => input.setTouch({ [key]: v });

  const down = (e: Event) => {
    e.preventDefault();
    set(true);
  };
  const up = (e: Event) => {
    e.preventDefault();
    set(false);
  };

  el.addEventListener('pointerdown', down);
  el.addEventListener('pointerup', up);
  el.addEventListener('pointerleave', up);
  el.addEventListener('pointercancel', up);
  el.addEventListener('contextmenu', (e) => e.preventDefault());
}
